import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  ChevronRight,
  Search,
  TrendingUp,
  X,
} from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import { getCommunityGrowthCommission } from "../../../../Redux/referral/slice";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import MrcCommissionTable from "./MrcCommissionTable";
import {
  formatEarningsCurrency,
  formatEarningsDate,
  normalizeEarningsStatus,
  statusLabel,
} from "../earningsAdapters";
import "./CommunityGrowthIncentives.css";

const statusClass = (status: string) =>
  `community-status community-status-${normalizeEarningsStatus(status)}`;

const MrcCommissons = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { communityGrowthCommission } = useSelector(
    (state: RootState) => state.referral,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (dateRange.from && !dateRange.to) return;

    dispatch(
      getCommunityGrowthCommission({
        page,
        limit,
        status: "ALL",
        sortOrder: "DESC",
        search: debouncedSearch || undefined,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [dispatch, page, limit, debouncedSearch, dateRange.from, dateRange.to]);

  const records = communityGrowthCommission.data || [];
  const totalRecords =
    communityGrowthCommission.pagination?.total ?? records.length;

  return (
    <section className="community-page">
      <div className="community-shell">
        <header className="community-page-header">
          <div className="community-title-wrap">
            <span className="community-title-icon">
              <TrendingUp size={23} />
            </span>
            <div>
              <span className="community-eyebrow">MY REWARDS</span>
              <h1>Community Growth Incentives</h1>
              <p>
                Track community growth achievements, SCGR incentives and
                bonus milestones.
              </p>
            </div>
          </div>
          <span className="community-count-pill">
            {totalRecords} records
          </span>
        </header>

        <div className="community-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>

          <div className="community-date-picker">
            <DateRangePicker
              value={dateRange}
              onChange={(v) => {
                setDateRange(v);
                setPage(1);
              }}
            />
          </div>

          <span className="community-toolbar-count">
            {records.length} shown
          </span>

          <div className="community-search">
            <Search size={17} />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search growth incentives..."
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <MrcCommissionTable data={records} />

        <div className="community-mobile-list">
          {records.map((item, index) => (
            <Link
              className="community-mobile-card"
              key={index}
              to="/MrcCommissonDetails"
            >
              <span className="community-mobile-index">{item.sNo}</span>
              <img
                src={item.productDetails?.image || "../../assets/img/women/women-4.png"}
                alt=""
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "../../assets/img/women/women-4.png";
                }}
              />
              <span className="community-mobile-copy">
                <span className="community-mobile-topline">
                  <span className={statusClass(item.receivedStatus)}>
                    {statusLabel(item.receivedStatus)}
                  </span>
                  <span className="community-mobile-date">
                    <CalendarDays size={14} />
                    {formatEarningsDate(item.date)}
                  </span>
                </span>
                <strong className="community-mobile-title">
                  {item.productDetails?.productName || "N/A"}
                </strong>
                <span className="community-mobile-description">
                  {item.referralMilestone}
                </span>
                <span className="community-mobile-bottomline">
                  <strong>
                    {formatEarningsCurrency(
                      item.netAmount ?? item.receivedSCGRCommission,
                    )}
                  </strong>
                  <span>
                    {item.orderNo || ""}
                    {item.tdsAmount != null &&
                      ` · TDS ${formatEarningsCurrency(item.tdsAmount)}`}
                  </span>
                </span>
              </span>
              <ChevronRight
                size={18}
                className="community-mobile-chevron"
              />
            </Link>
          ))}
        </div>

        {records.length === 0 && (
          <div className="community-empty-state">
            <TrendingUp size={30} />
            <strong>No community growth incentives found</strong>
            <span>Change the search or selected date range.</span>
          </div>
        )}

        <Pagenation
          currentPage={page}
          totalPages={communityGrowthCommission.pagination?.totalPages || 0}
          onPageChange={setPage}
        />
      </div>
    </section>
  );
};

export default MrcCommissons;
